import { useEffect, useRef, useState } from "react";
import useGet from "../../useGet";
import useCreate from "../../useCreate";
import toast from "react-hot-toast";
import { formatDateWithHyphens } from "../../../utilities";

const useDownloadReports = () => {
  const [brandOptions, setBrandOptions] = useState([]);
  const [brandHasMore, setBrandHasMore] = useState(true);
  const [brandPageNo, setBrandPageNo] = useState(0);
  const [brandSearchQuery, setBrandSearchQuery] = useState("");
  const [brandId, setBrandId] = useState(null);

  const {
    data: brandData,
    setParamsObject: setBrandParams,
    data: brandQuery,
  } = useGet({
    url: "brand/list",
    initialParams: {
      pageNo: 0,
      limit: 10,
      searchQuery: "",
    },
    onSuccess: (response) => {
      const formattedOptions =
        response?.data?.map((brand) => ({
          value: brand.brand_id,
          label: brand.name,
        })) || [];

      if (brandPageNo === 0) {
        setBrandOptions([
          { value: "", label: "All Brands" },
          ...formattedOptions,
        ]);
      } else {
        setBrandOptions((prev) => [...prev, ...formattedOptions]);
      }

      setBrandHasMore(
        formattedOptions.length > 0 &&
          response?.totalCount > brandOptions.length
      );
    },
    onError: (err) => {
      console.error("Brand fetch error:", err);
    },
  });

  // Function to fetch brands with pagination and search
  const fetchBrands = (query = "", reset = false) => {
    const pageToFetch = reset ? 0 : brandPageNo;

    if (reset) {
      setBrandPageNo(0);
      setBrandOptions([]);
    }

    setBrandParams({
      pageNo: pageToFetch,
      limit: 10,
      searchQuery: query,
    });

    if (!reset) {
      setBrandPageNo((prev) => prev + 1);
    }

    setBrandSearchQuery(query);
  };

  // Handle brand selection
  const handleBrandSelect = (brandIdParam) => {
    setBrandId(brandIdParam);
  };

  //   Date state
  const [fromDate, setFromDate] = useState(() => {
    const date = new Date();
    date.setDate(date.getDate() - 90);
    return date;
  });
  const [toDate, setToDate] = useState(new Date());

  // Download code
  const { setDataToServer: downloadReq } = useCreate({
    url: "report/sales",
    responseAsBlob: true,
    onSuccess: (data) => {
      const blob = new Blob([data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "sales_report.csv";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url); // clean up

      toast.success("Report downloaded successfully", {
        duration: 2500,
      });
    },
    onError: (error) => {
      toast.error("Error occurred while downloading report: " + error.message);
    },
  });
  const isDownloading = downloadReq.isLoading;

  const toastId = useRef(null);

  useEffect(() => {
    if (isDownloading) {
      toastId.current = toast.loading("Downloading report...");
    } else {
      toast.dismiss(toastId.current);
    }

    return () => {
      // Clean up on unmount or state change
      if (toastId.current) toast.dismiss(toastId.current);
    };
  }, [isDownloading]);

  const downloadReports = () => {
    if (!fromDate || !toDate) {
      toast.error("Please select both from and to dates");
      return;
    }
    downloadReq.mutate({
      brand_id: brandId || "",
      from_date: formatDateWithHyphens(fromDate),
      to_date: formatDateWithHyphens(toDate),
    });
  };

  // Code for fetching reports
  const [enableFetchQuery, setEnableFetchQuery] = useState(false);
  const {
    data: reportsReq,
    paramsObject,
    setParamsObject,
  } = useGet({
    url: "report/sales_report_listing",
    enabled: enableFetchQuery,
    method: "POST",
    initialParams: {
      pageNo: 0,
      limit: 10,
    },
  });
  const reports = reportsReq?.data?.data || [];
  const isReportListLoading = reportsReq?.isLoading;
  const pagination = reportsReq?.data || {};

  const fetchReports = () => {
    setEnableFetchQuery(true);
    setParamsObject((prev) => {
      return {
        ...prev,
        brand_id: brandId || "",
        from_date: formatDateWithHyphens(fromDate),
        to_date: formatDateWithHyphens(toDate),
      };
    });
  };

  const handlePageClick = (val) => {
    let pageNo = {
      ...paramsObject,
      pageNo: val.selected,
    };
    // setCurrentPage(val.selected);
    setParamsObject(pageNo);
  };

  return {
    brandOptions,
    brandHasMore,
    fetchBrands,
    brandQuery,
    brandId,
    handleBrandSelect,
    // Date states
    fromDate,
    setFromDate,
    toDate,
    setToDate,
    // Download function
    downloadReports,
    // Fetch reports
    fetchReports,
    reports,
    enableFetchQuery,
    isReportListLoading,
    pagination,
    handlePageClick,
  };
};

export default useDownloadReports;
